import {
  Dialog,
  Heading,
  IDialogProps,
  Stack,
  SubmissionForm,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { Rollout } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useCreateRollout } from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { convertToDateTime } from "../utils";
import { RolloutForm } from "./RolloutForm";
import {
  rolloutFormSchema,
  rolloutFormValidator
} from "./RolloutFormValidator";
import { RolloutFormValues } from "./rolloutFormValues";

const parseRollout = (rollout?: Rollout): RolloutFormValues => {
  return {
    ...rollout,
    initialDownloadTimeStartUtc: rollout?.initialDownloadStartUtc?.toTimeInputFormat(),
    initialDownloadTimeEndUtc: rollout?.initialDownloadEndUtc?.toTimeInputFormat(),
    initialInstallTimeStartUtc: rollout?.initialInstallStartUtc?.toTimeInputFormat(),
    initialInstallTimeEndUtc: rollout?.initialInstallEndUtc?.toTimeInputFormat(),
    initialDownloadDateStartUtc: rollout?.initialDownloadStartUtc?.toJSDate(),
    initialDownloadDateEndUtc: rollout?.initialDownloadEndUtc?.toJSDate(),
    initialInstallDateStartUtc: rollout?.initialInstallStartUtc?.toJSDate(),
    initialInstallDateEndUtc: rollout?.initialInstallEndUtc?.toJSDate(),
    rolloutPackages: []
  };
};

interface DuplicateRolloutDialogProps extends IDialogProps {
  onDismiss: () => void;
  rollout: Rollout;
}

export const DuplicateRolloutDialog: React.FC<DuplicateRolloutDialogProps> = ({
  onDismiss,
  rollout,
  ...props
}) => {
  const { mutateAsync: createRollout } = useCreateRollout();
  const theme = useTheme();

  const onSubmit = async (formData: RolloutFormValues) => {
    const {
      displayName,
      isActive,
      initialDownloadTimeStartUtc,
      initialDownloadTimeEndUtc,
      initialInstallTimeStartUtc,
      initialInstallTimeEndUtc,
      initialDownloadDateStartUtc,
      initialDownloadDateEndUtc,
      initialInstallDateStartUtc,
      initialInstallDateEndUtc
    } = formData;

    const downloadStartUtc = convertToDateTime(
      initialDownloadDateStartUtc!,
      initialDownloadTimeStartUtc!
    );

    const downloadEndUtc = convertToDateTime(
      initialDownloadDateEndUtc!,
      initialDownloadTimeEndUtc!
    );

    const installStartUtc = convertToDateTime(
      initialInstallDateStartUtc!,
      initialInstallTimeStartUtc!
    );

    const installEndUtc = convertToDateTime(
      initialInstallDateEndUtc!,
      initialInstallTimeEndUtc!
    );

    await createRollout({
      displayName,
      isBase64Encoded: true,
      isActive: isActive!,
      deploymentRingId: rollout.deploymentRingId,
      initialDownloadStartUtc: downloadStartUtc.toISO(),
      initialDownloadEndUtc: downloadEndUtc.toISO(),
      initialInstallStartUtc: installStartUtc.toISO(),
      initialInstallEndUtc: installEndUtc.toISO(),
      rolloutId: rollout.id // Duplicating rollout packages and desired config from this rolloutId
    });

    onDismiss();
  };

  const newRollout: Rollout = {
    deploymentRingId: rollout.deploymentRingId,
    isActive: false,
    displayName: "",
    rolloutPackages: []
  };

  return (
    <Dialog
      dialogContentProps={{
        showCloseButton: true,
        title: (
          <Stack>
            <Heading variant="modal-heading">Duplicate Rollout</Heading>
            <Stack horizontal tokens={{ childrenGap: theme.spacing.s1 }}>
              <Text variant="small" bold>
                Duplicating:
              </Text>
              <Text variant="small"> {rollout.displayName}</Text>
            </Stack>
            <Text variant="small">({rollout.id})</Text>
          </Stack>
        )
      }}
      minWidth={780}
      onDismiss={onDismiss}
      {...props}
    >
      <SubmissionForm<RolloutFormValues>
        onSubmit={onSubmit}
        initialValues={parseRollout(rollout)}
        validate={values =>
          rolloutFormValidator.validate(values, rolloutFormSchema)
        }
        hideButtons
      >
        {() => <RolloutForm rollout={newRollout} duplicate={true} />}
      </SubmissionForm>
    </Dialog>
  );
};
